import { type Ref } from 'vue'
import { ConsultationSummaryReportService } from '@/services/telehealthApi/ConsultationSummaryReportService'
import { useNoticeStore } from '@/stores/notice'
import { NoticeType } from '@/stores/models/Notice'
import { useI18n } from 'vue-i18n'
import type { ConsultationFilters } from '@/composables/consultation/useCompletedConsultationFilters'

function useSummaryReportExporter(filters: Ref<ConsultationFilters>) {
  const { t } = useI18n()

  function showGeneratingPlaceholder(tab: Window) {
    const message = t('Generating report...')
    tab.document.title = message
    tab.document.body.style.cssText =
      'margin:0;height:100vh;display:flex;align-items:center;justify-content:center;font-family:sans-serif;color:#555'
    tab.document.body.textContent = message
  }

  // The tab must be opened before the first await, otherwise the click no longer counts as a user
  // gesture and popup blockers reject it. If it is blocked anyway, fall back to a download.
  async function exportSummaryReports(): Promise<void> {
    const tab = window.open('', '_blank')
    if (tab) showGeneratingPlaceholder(tab)

    try {
      const { file, fileName } =
        await ConsultationSummaryReportService.exportConsultationsSummaryReport({
          filter: {
            createdAt: {
              from: filters.value.selectedRange?.[0]?.toISOString() ?? null,
              to: filters.value.selectedRange.at(-1)?.toISOString() ?? null
            },
            patients: filters.value.selectedPatientsIds
          }
        })
      const blob = new Blob([file], {
        type: 'application/pdf'
      })

      const url = URL.createObjectURL(blob)

      if (tab) {
        // Not revoked: the PDF viewer may re-read the blob later for save/print.
        tab.location.href = url
        return
      }

      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()

      a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      tab?.close()
      useNoticeStore().add({
        type: NoticeType.ERROR,
        message: t('Export failed. Please try again') + '.'
      })
    }
  }

  return {
    exportSummaryReports
  }
}

export default useSummaryReportExporter
